import { Box, Grid, LinearProgress, Typography } from "@mui/material";
import type { SpecimenAuditAnalyticsResponse } from "../../../services/api";
import { colors } from "../../../theme/colors";
import { statusDisplayLabel } from "../utils";

type Props = {
  analytics?: SpecimenAuditAnalyticsResponse;
};

// Lightweight column layout to avoid MUI Stack's default spacing overrides.
const Stack = ({ children }: { children: React.ReactNode }) => (
  <Box sx={{ display: "flex", flexDirection: "column" }}>{children}</Box>
);

const Card = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <Box
    sx={{
      height: "100%",
      border: `1px solid ${colors.grey}`,
      borderRadius: "8px",
      backgroundColor: "#ffffff",
      p: 2.5,
    }}
  >
    <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 1.5 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const Empty = ({ label }: { label: string }) => (
  <Typography sx={{ fontSize: 14, color: colors.grey }}>{label}</Typography>
);

const AnalyticsBand = ({ analytics }: Props) => {
  if (!analytics) return null;

  const maxTransitionCount = Math.max(
    1,
    ...analytics.transition_matrix.map((cell) => cell.count)
  );
  const maxReasonCount = Math.max(
    1,
    ...analytics.top_discrepancy_reasons.map((reason) => reason.count)
  );

  return (
    <Grid container spacing={2.5}>
      <Grid item xs={12} md={4}>
        <Card title="Transition matrix">
          {analytics.transition_matrix.length ? (
            <Stack>
              {analytics.transition_matrix.map((cell, index) => {
                const intensity = cell.count / maxTransitionCount;
                return (
                  <Box
                    // Index-suffixed: the API does not guarantee unique
                    // previous/current pairs, and a duplicate would collide.
                    key={`${cell.previous_status}-${cell.current_status}-${index}`}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      py: 0.75,
                      borderBottom: `1px solid ${colors.lightestGrey}`,
                    }}
                  >
                    <Typography sx={{ fontSize: 13, color: colors.dark }}>
                      {statusDisplayLabel(cell.previous_status)} →{" "}
                      {statusDisplayLabel(cell.current_status)}
                    </Typography>
                    <Box
                      sx={{
                        minWidth: 34,
                        textAlign: "center",
                        px: 1,
                        py: 0.25,
                        borderRadius: "6px",
                        fontSize: 13,
                        fontWeight: 600,
                        color: intensity > 0.55 ? "#fff" : colors.lighterBlack,
                        backgroundColor: `rgba(17, 17, 17, ${0.08 + intensity * 0.82})`,
                      }}
                    >
                      {cell.count}
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          ) : (
            <Empty label="No transitions for the selected filters." />
          )}
        </Card>
      </Grid>

      <Grid item xs={12} md={4}>
        <Card title="Top discrepancy reasons">
          {analytics.top_discrepancy_reasons.length ? (
            analytics.top_discrepancy_reasons.map((reason, index) => (
              // Reason is freeform text, so it is not a reliable unique key.
              <Box key={`${reason.reason}-${index}`} sx={{ mb: 1.75 }}>
                <Box
                  sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}
                >
                  <Typography sx={{ fontSize: 13, color: colors.dark }}>
                    {reason.reason}
                  </Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                    {reason.count}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={(reason.count / maxReasonCount) * 100}
                  sx={{
                    height: 8,
                    borderRadius: "6px",
                    backgroundColor: colors.lightGrey,
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: colors.darkModalBackground,
                      borderRadius: "6px",
                    },
                  }}
                />
              </Box>
            ))
          ) : (
            <Empty label="No discrepancy reasons recorded." />
          )}
        </Card>
      </Grid>

      <Grid item xs={12} md={4}>
        <Card title="SLA breaches">
          {analytics.sla_breaches.length ? (
            <Stack>
              {analytics.sla_breaches.map((breach) => (
                <Box
                  key={breach.specimen_id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    py: 0.9,
                    borderBottom: `1px solid ${colors.lightestGrey}`,
                  }}
                >
                  <Typography sx={{ fontSize: 13, color: colors.dark }}>
                    {breach.specimen_qr ?? breach.specimen_id}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography sx={{ fontSize: 13, color: colors.grey }}>
                      {breach.lab_handoff_minutes}m
                    </Typography>
                    <Box
                      sx={{
                        px: 1,
                        py: 0.25,
                        borderRadius: "6px",
                        fontSize: 12,
                        fontWeight: 600,
                        color: colors.darkerRed,
                        backgroundColor: colors.lightRed,
                      }}
                    >
                      +{breach.breach_by_minutes}m
                    </Box>
                  </Box>
                </Box>
              ))}
            </Stack>
          ) : (
            <Empty label="No SLA breaches. Nice." />
          )}
        </Card>
      </Grid>
    </Grid>
  );
};

export default AnalyticsBand;
